import { AxiosInstance } from "axios";
import { useAuthStore } from "../store/auth.store";

/**
 * Навешивает на axios-клиент стандартные auth-интерсепторы:
 *  - request: подставляет актуальный Bearer-токен из стора;
 *  - response: на 401/403 один раз пытается обновить токен через refresh()
 *    и повторяет исходный запрос. Если refresh не дал токен — пробрасывает ошибку.
 *
 * ВАЖНО про 403: бэкенд (user/app/api/auth/auth_bearer.py) отдаёт 403 FORBIDDEN,
 * когда токена нет / он не Bearer; а 401 (UNAUTHENTICATED) — когда токен протух.
 * Поэтому refresh нужно пытаться и на 401, и на 403, иначе «не залогинен» (403)
 * не запустит обновление токена. Защита `_retry` гарантирует один повтор — цикла
 * не будет, даже если refresh не помог.
 *
 * Логика повторяет ту, что в api.auth.ts, чтобы meters/payments/organization
 * клиенты не падали молча при истечении access-токена.
 */
export function attachAuthInterceptors(client: AxiosInstance) {
  client.interceptors.request.use((config) => {
    const authStore = useAuthStore.getState();
    config.headers["Authorization"] = `Bearer ${authStore.accessToken}`;
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        const { refresh } = useAuthStore.getState();
        const { accessToken } = await refresh();
        if (accessToken) {
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          return client(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );
}
